/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { IPatient } from '@ahryman40k/ts-fhir-types/lib/R4/Resource';

import pearlApi from '../api/pearlApi';
import { fetchPatient, makePearlApiFhirCallAndUnwrapPaged } from '../Fhir/fhir_network';

export async function fetchAndAnonymise(
  mrn: string,
  fetchObservations: boolean,
  server: string | undefined,
  signal: AbortSignal,
  retryAttempts = 1
) {
  for (let i = 0; i < retryAttempts; i++) {
    try {
      return await fetchAndAnonymise_(mrn, fetchObservations, server, signal);
    } catch (e) {
      if (i === retryAttempts - 1) {
        throw e;
      }
    }
  }
  throw new Error('Should never get here');
}

function ageToAgeGroup(age: number) {
  if (age < 10) return '<10';
  if (age < 20) return '10-19';
  if (age < 30) return '20-29';
  if (age < 40) return '30-39';
  if (age < 50) return '40-49';
  if (age < 60) return '50-59';
  if (age < 70) return '60-69';
  else return '70+';
}
export async function fetchAndAnonymise_(
  mrn: string,
  fetchObservations: boolean,
  server: string | undefined,
  signal: AbortSignal
) {
  const patientReal = await fetchPatient(mrn, signal);
  if (!patientReal) {
    throw new Error('Patient not found for MRN:' + mrn);
  }
  // Make a new patient, to anonymize the data
  const birthYearFuzzed = patientReal.birthDate ? parseInt(patientReal.birthDate.slice(0, 10) || '0') : undefined;
  const age = birthYearFuzzed ? ageToAgeGroup(Math.round(new Date().getFullYear() - birthYearFuzzed)) : '(unknown)';
  const gender = patientReal.gender;
  const diagnosticReports = await fetchDiagnosticReportsAdmin(mrn, patientReal, fetchObservations, server, signal);
  if (!diagnosticReports) {
    throw new Error('No diagnostic reports found');
  }
  return { age, gender, diagnosticReports };
}

export async function fetchDiagnosticReportsAdmin(
  mrn: string,
  patientReal: IPatient,
  fetchObservations: boolean,
  server: string | undefined,
  signal: AbortSignal
) {
  // Now lets fetch all of their labs etc
  const elements =
    'id,code,result,resourceType,presentedForm,valueQuantity,effectiveDateTime,category,referenceRange,valueCodeableConcept,valueRatio,valueString,valueBoolean,valueDateTime,dataAbsentReason,interpretation,note,issued';
  const fetchObservationsStr = fetchObservations ? '&_include=DiagnosticReport:result:Observation' : '';
  const str = `DiagnosticReport?patient=${patientReal.id}${fetchObservationsStr}&_elements=${elements}`;
  const diagnosticReports = await makePearlApiFhirCallAndUnwrapPaged('/fhir/' + str, mrn, server || '*', signal);
  return diagnosticReports;
}

/** Use the fhir api to do a bulk download
 *  See https://fhir.epic.com/Specifications?api=10169
 *
 * Note we must provide the headers:
 *    Accept:application/fhir+json
 *    Prefer:respond-async
 * I have asked Raul to do this.
 *
 * From:
 *
 * https://fhir.epic.com/Documentation?docId=fhir_bulk_data
 *
 * For groups of under one hundred patients, check the request status every ten minutes,
 * or use exponential backoff. For groups of over one hundred patients, check the status
 * every thirty minutes, or use exponential backoff. More information on exponential backoff
 * can be found in the bulk data export specification.
 * After retrieving all of the resource file content, use the FHIR Bulk Data Delete Request
 * API to allow the server to clean up the data from your request.
 *
 */
export async function fetchByFhirBulkDownload(
  groupId: string,
  FhirPageWidgetType: string,
  signal: AbortSignal,
  setProgress: (x: number) => void
) {
  const url = await fhirKickOffBulkFhirDownload(groupId, FhirPageWidgetType, signal);
  // Now we need to poll the url for the data, asking every 10 minutes
  let x = 1;
  while (true) {
    await new Promise((resolve) => setTimeout(resolve, 10 * 60 * 1000));
    const data = await fetchBulkData(url, signal);
    setProgress(x++);
    if (data) return data;
  }
}

/** Kick off a bulk data download
 * See: https://fhir.epic.com/Specifications?api=10169
 *
 * To kick off a bulk data download, execute a bulk data kick-off request for
 * either all resources available to the client, except Binary, or narrow to a
 * subset by using the “_type” parameter. Note that headers “Accept” with
 * value “application/fhir+json” and “Prefer” with value “respond-async” are required.
 * If the kick-off request is accepted, the response is returned with a status of
 * “202 Accepted” and a “Content-Location” HTTP header containing the URL of an
 * endpoint for bulk data status or bulk data delete requests. The URL contains
 * a unique ID referred to as the bulk request ID.
 *
 * Example response:
 *
 *   HTTP/1.1 202 Accepted
 *   Cache-Control: no-cache,no-store
 *   Pragma: no-cache
 *   Content-Type: text/plain; charset=utf-8
 *   Content-Location: https://hostname/instance/api/FHIR/BulkRequest/0000000000176E6DC7DB51C0082DA988
 *
 * --------------------------------
 *
 * FhirPageWidgetType is, .e.g., 'DiagnosticReport,Patient,Observation'
 */
async function fhirKickOffBulkFhirDownload(groupId: string, FhirPageWidgetType: string, signal: AbortSignal) {
  const str = `Group/${groupId}/$export` + (FhirPageWidgetType ? '?_type=' + FhirPageWidgetType : '');
  const fhirConfig = {
    headers: {
      'X-Pearl-MRN': 'dummy_mrn',
      'X-Pearl-FHIR-Servers': '*',
      // Header. I don't actually know if Raul will forward these headers though
      Accept: 'application/fhir+json',
      Prefer: 'respond-async',
    },
    signal,
  };
  const res = (await pearlApi.get('/fhir/' + str, fhirConfig)) as { data: { data: { response: any }[] } };
  const response = res?.data?.data[0]?.response;

  if (!response) throw new Error('Something went wrong with response');
  // Check if the response is a 202 (Accepted)
  if (response.status === 202) {
    // We are good, get the location
    const location = response.headers?.location;
    if (!location) {
      throw new Error('No location in response');
    }
    // The url includes the epic.com hostname which we need to remove.  Remove the .*/FHIR/ part
    const url = location.replace(/.*\/FHIR\//, '');
    console.log('url:', url);
    return url;
  } else {
    throw new Error('Unexpected response status:' + response.status);
  }
}

/**
 * Fetch the bulk data
 * This won't actually return the data, but will return the URLs to download the data
 *
 * {
 *   "transactionTime": "2021-06-23T16:39:52Z",
 *   "request": "https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4/Group/eIscQb2HmqkT.aPxBKDR1mIj3721CpVk1suC7rlu3yX83/$export?_type=patient,encounter,condition",
 *   "requiresAccessToken": "true",
 *   "output": [
 *       {
 *           "type": "Patient",
 *           "url": "https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/BulkRequest/9ED3042CD44111EB84F2D2068206269D/e19upATM-PTKGZuHsy04IUQ3"
 *       },
 *       {
 *           "type": "Condition",
 *           "url": "https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/BulkRequest/9ED3042CD44111EB84F2D2068206269D/e8D8N1JwB3qCiHJkmeV.98w3"
 *       }
 *   ],
 *   "error": [
 *       {
 *           "type": "OperationOutcome",
 *           "url": "https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/BulkRequest/9ED3042CD44111EB84F2D2068206269D/eKbGgVw9pUn6xIiB8kZ756w3"
 *       }
 *   ]
 * }
 */
async function fetchBulkData(url: string, signal: AbortSignal) {
  // Now we need to poll the url for the data
  const fhirConfig = {
    headers: {
      'X-Pearl-MRN': 'dummy_mrn',
      'X-Pearl-FHIR-Servers': '*',
    },
    signal,
  };
  // Note we want to poll /fhir/ + url and we DONT want Raul's backend to add /R4
  // I've messaged Raul to ask him for a workaround - i.e. hardcode not adding /R4
  const res = await pearlApi.get('/fhir/' + url, fhirConfig);

  console.log('res:', res);
  // If the bulk data request has not finished processing, the response body is empty.
  if (!res) {
    throw new Error('No response');
  }
  if (res.status === 202) {
    throw new Error('Still processing');
  }
  if (!res.data) return null; // Call again with exponential backoff

  const data = res.data as {
    transactionTime: string;
    request: string;
    requiresAccessToken: string;
    output: { type: string; url: string }[];
    error: { type: string; url: string }[];
  };
  for (const output of data.output) {
    // Fetch the data
    const url = output.url.replace(/.*\/FHIR\//, '');
    const res = await pearlApi.get('/fhir/' + url, fhirConfig);
    (output as any).res = res.data;
  }
  // Might as well grab the errors too
  for (const error of data.error) {
    const url = error.url.replace(/.*\/FHIR\//, '');
    const res = await pearlApi.get('/fhir/' + url, fhirConfig);
    (error as any).res = res.data;
  }
  return data;
}
